// AdminAudit — Audit log of all admin actions
// Filter by admin, action type, severity, date

import { useState } from 'react';

interface LogEntry {
  id: string; admin: string; action: string; target: string; targetType: 'user' | 'bot' | 'payout' | 'kyc' | 'content' | 'settings';
  time: string; severity: 'low' | 'medium' | 'high'; ip: string;
}

const LOG: LogEntry[] = [
  { id: 'l1',  admin: 'admin@funderpro.live',  action: 'Approved KYC',          target: 'Aisha Khan',     targetType: 'kyc',     time: '5m ago',   severity: 'low',    ip: '203.0.113.42' },
  { id: 'l2',  admin: 'admin@funderpro.live',  action: 'Banned user',           target: 'scammer',         targetType: 'user',    time: '12m ago',  severity: 'high',   ip: '203.0.113.42' },
  { id: 'l3',  admin: 'support@funderpro.live',action: 'Sent message',          target: 'Sofia Petrov',    targetType: 'user',    time: '23m ago',  severity: 'low',    ip: '203.0.113.42' },
  { id: 'l4',  admin: 'admin@funderpro.live',  action: 'Force-closed trade',    target: 'Aiden Wright · GC',targetType: 'user',   time: '1h ago',   severity: 'medium', ip: '203.0.113.42' },
  { id: 'l5',  admin: 'admin@funderpro.live',  action: 'Approved payout',       target: 'Brandon Lee · $1240', targetType: 'payout', time: '2h ago', severity: 'low', ip: '203.0.113.42' },
  { id: 'l6',  admin: 'admin@funderpro.live',  action: 'Removed content',       target: 'Post by fakeuser', targetType: 'content', time: '3h ago',   severity: 'medium', ip: '203.0.113.42' },
  { id: 'l7',  admin: 'admin@funderpro.live',  action: 'Activated kill switch', target: 'Aiden Wright',     targetType: 'user',    time: '4h ago',   severity: 'high',   ip: '203.0.113.42' },
  { id: 'l8',  admin: 'admin@funderpro.live',  action: 'Takedown bot',          target: 'PumpKing',         targetType: 'bot',     time: '5h ago',   severity: 'high',   ip: '203.0.113.42' },
  { id: 'l9',  admin: 'support@funderpro.live',action: 'Reset 2FA',             target: 'Marcus Chen',      targetType: 'user',    time: '6h ago',   severity: 'medium', ip: '203.0.113.42' },
  { id: 'l10', admin: 'admin@funderpro.live',  action: 'Bulk-approved KYC',     target: '23 users',        targetType: 'kyc',     time: '1d ago',   severity: 'low',    ip: '203.0.113.42' },
  { id: 'l11', admin: 'admin@funderpro.live',  action: 'Changed fee structure', target: 'All users',        targetType: 'settings', time: '2d ago',  severity: 'high',   ip: '203.0.113.42' },
];

const SEV_COLOR = { low: '#34C759', medium: '#FF9500', high: '#FF3B30' };
const ACTION_META: Record<string, { icon: string; color: string }> = {
  'Approved KYC':         { icon: '✓', color: '#34C759' },
  'Banned user':          { icon: '🚫', color: '#FF3B30' },
  'Sent message':         { icon: '✉️', color: '#FF9500' },
  'Force-closed trade':   { icon: '✖', color: '#FF3B30' },
  'Approved payout':      { icon: '✓', color: '#34C759' },
  'Removed content':      { icon: '🗑', color: '#FF3B30' },
  'Activated kill switch':{ icon: '🚨', color: '#FF3B30' },
  'Takedown bot':         { icon: '🤖', color: '#FF3B30' },
  'Reset 2FA':            { icon: '🔐', color: '#5856D6' },
  'Bulk-approved KYC':    { icon: '✓', color: '#34C759' },
  'Changed fee structure':{ icon: '⚙️', color: '#FF9500' },
};

export function AdminAudit() {
  const [filter, setFilter] = useState<'all' | 'low' | 'medium' | 'high'>('all');
  const [actionFilter, setActionFilter] = useState('all');

  const filtered = LOG.filter((l) => {
    if (filter !== 'all' && l.severity !== filter) return false;
    if (actionFilter !== 'all' && l.action !== actionFilter) return false;
    return true;
  });

  return (
    <div className="space-y-4 max-w-7xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-[22px] font-bold text-white">Audit log</h1>
          <p className="text-[12px] text-white/50">{filtered.length} of {LOG.length} events · All actions are recorded and immutable</p>
        </div>
        <button onClick={() => alert('Exporting CSV...')} data-testid="export-audit" className="h-9 rounded-[8px] bg-white/5 border border-white/10 px-3 text-[12px] font-semibold text-white/80 active:bg-white/10">Export CSV</button>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} data-testid="audit-action" className="h-9 rounded-[8px] bg-white/5 border border-white/10 px-3 text-[12px] text-white focus:outline-none">
          <option value="all">All actions</option>
          {Object.keys(ACTION_META).map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <div className="flex items-center gap-1">
          {(['all', 'low', 'medium', 'high'] as const).map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`auditfilter-${f}`} className={['rounded-full px-3 py-1.5 text-[11px] font-semibold capitalize transition', filter === f ? 'bg-white text-black' : 'bg-white/5 text-white/60 active:bg-white/10'].join(' ')}>
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 overflow-hidden">
        <div className="divide-y divide-white/5">
          {filtered.map((l) => {
            const m = ACTION_META[l.action] || { icon: '•', color: '#8E8E93' };
            return (
              <div key={l.id} data-testid={`log-${l.id}`} className="grid grid-cols-12 gap-2 px-4 py-3 items-center text-[11px]">
                <div className="col-span-1 flex items-center justify-center w-8 h-8 rounded-full" style={{ backgroundColor: m.color + '20' }}>
                  <span className="text-[16px]">{m.icon}</span>
                </div>
                <div className="col-span-2">
                  <div className="text-white font-semibold">{l.action}</div>
                  <div className="text-[10px] text-white/40">by {l.admin}</div>
                </div>
                <div className="col-span-3 text-white truncate">{l.target}</div>
                <div className="col-span-1"><span className="rounded-full bg-white/5 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-white/60">{l.targetType}</span></div>
                <div className="col-span-1"><span className="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase" style={{ backgroundColor: SEV_COLOR[l.severity] + '20', color: SEV_COLOR[l.severity] }}>{l.severity}</span></div>
                <div className="col-span-2 text-white/60 text-[10px] font-mono truncate">{l.ip}</div>
                <div className="col-span-2 text-right text-white/50 text-[10px]">{l.time}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
